import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { 
  Thermometer, 
  Droplets, 
  Power, 
  Snowflake, 
  Sun, 
  Leaf, 
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Calendar,
  AlertTriangle,
  Zap,
  TrendingDown,
  Settings,
  MapPin,
  Loader2,
  X,
  Check,
  CheckSquare,
  Square
} from 'lucide-react';
import { 
  getWeatherByLocation, 
  TAIWAN_LOCATIONS, 
  getSavedLocation, 
  saveLocation,
  getLocationByIp,
  type WeatherData 
} from '../../services/weatherApi';
import { ultronTuringApi } from '../../services/ultronTuringApi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
} from 'recharts';

interface DeviceZone {
  id: string;
  name: string;
  devices: string[];
}

interface Device {
  deviceId: string;
  baseSN: string;
  deviceSN?: string;
  sn?: string;
  deviceType: string;
  displayName?: string;
  deviceName?: string;
  name?: string;
  nickname?: string;
  isOn?: boolean;
  temperature?: number;
  setpoint?: number;
  humidity?: number;
  mode?: string;
  fanSpeed?: string;
  traits?: string[];
  attributes?: any;
  watt?: number;
  real_power?: number;
}

interface ClimateDataPoint {
  hour: string;
  temp: number | null;
  humidity: number | null;
}

interface HVACControlViewProps {
  devices: Device[];
  zones: DeviceZone[];
  climateChartData?: ClimateDataPoint[];
  onOpenZoneSelector?: () => void;
  selectedZoneCount?: number;
  allDevices?: Device[];
}

// Initial demo zone data for HVAC
const initialDemoZones = [
  { id: 'demo-1', name: 'Floor 1 - Lobby', currentTemp: 22, targetTemp: 22, humidity: 45, mode: 'cool' as const, isOn: true },
  { id: 'demo-2', name: 'Floor 1 - Conference A', currentTemp: 22, targetTemp: 22, humidity: 44, mode: 'cool' as const, isOn: false },
  { id: 'demo-3', name: 'Floor 2 - Open Office', currentTemp: 23, targetTemp: 23, humidity: 42, mode: 'cool' as const, isOn: true },
  { id: 'demo-4', name: 'Floor 2 - Break Room', currentTemp: 24, targetTemp: 22, humidity: 50, mode: 'heat' as const, isOn: true },
  { id: 'demo-5', name: 'Floor 3 - Executive', currentTemp: 22, targetTemp: 22, humidity: 44, mode: 'auto' as const, isOn: true },
  { id: 'demo-6', name: 'Floor 3 - Server Room', currentTemp: 18, targetTemp: 18, humidity: 35, mode: 'cool' as const, isOn: true },
];

// Generate 24-hour temperature history data
const generateTempHistoryData = () => {
  const data = [];
  const hours = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'];
  const setpoint = 23;
  
  for (let i = 0; i < hours.length; i++) {
    const variation = Math.sin(i * 0.8) * 3;
    data.push({
      time: hours[i],
      current: Math.round((22 + variation + Math.random() * 2) * 10) / 10,
      setpoint: setpoint,
    });
  }
  return data;
};

// Demo consumption values for zones
const DEMO_CONSUMPTION_VALUES = [45, 165, 120, 85, 95, 145, 78, 132, 56, 110];

const HVACControlView: React.FC<HVACControlViewProps> = ({ 
  devices, 
  zones, 
  climateChartData,
  onOpenZoneSelector,
  selectedZoneCount,
  allDevices
}) => {
  const [ecoMode, setEcoMode] = useState(false);
  const [zoneStates, setZoneStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    initialDemoZones.forEach(z => { states[z.id] = z.isOn; });
    return states;
  });
  const [zoneTargets, setZoneTargets] = useState<Record<string, number>>(() => {
    const targets: Record<string, number> = {};
    initialDemoZones.forEach(z => { targets[z.id] = z.targetTemp; });
    return targets;
  });
  const [zoneModes, setZoneModes] = useState<Record<string, 'cool' | 'heat' | 'auto'>>(() => {
    const modes: Record<string, 'cool' | 'heat' | 'auto'> = {};
    initialDemoZones.forEach(z => { modes[z.id] = z.mode; });
    return modes;
  });
  
  // Command loading states
  const [isCommandLoading, setIsCommandLoading] = useState(false);
  const commandDebounce = useRef<NodeJS.Timeout | null>(null);
  
  const ZONE_ENERGY_METER_MAP_KEY = 'hvac-zone-energy-meters';
  const [zoneEnergyMeterMap, setZoneEnergyMeterMap] = useState<Record<string, string[]>>(() => {
    try {
      const saved = localStorage.getItem(ZONE_ENERGY_METER_MAP_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showZoneEnergyMeterModal, setShowZoneEnergyMeterModal] = useState(false);
  const [selectedZoneForEnergy, setSelectedZoneForEnergy] = useState<string | null>(null);
  const [tempSelectedZoneEnergyMeters, setTempSelectedZoneEnergyMeters] = useState<string[]>([]);
  const [zoneEnergyMeterSearch, setZoneEnergyMeterSearch] = useState('');
  
  
  const meterSourceDevices = allDevices || devices;
  const zoneEnergyPowerMeters = useMemo(() => 
    meterSourceDevices.filter(d => {
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      const hasPowerData = d.watt !== undefined || (d as any).wattHour !== undefined || d.real_power !== undefined;
      const deviceType = (d.deviceType || '').toLowerCase();
      const isPowerType = deviceType.includes('power') || deviceType.includes('energy') || deviceType.includes('meter');
      return hasPowerCapability || hasPowerData || isPowerType;
    }), [meterSourceDevices]);

  const zoneEnergyMeterInfo = useMemo(() => zoneEnergyPowerMeters.map(d => ({
    id: d.deviceId,
    name: d.deviceName || d.nickname || d.deviceId,
    power: d.watt ?? d.real_power ?? 0,
    baseSN: d.baseSN,
    locationName: (d as any).locationName || (d as any).location || d.attributes?.locationName || ''
  })), [zoneEnergyPowerMeters]);

  const powerById = useMemo(() => {
    const map = new Map<string, number>();
    zoneEnergyMeterInfo.forEach(m => {
      map.set(m.id, m.power || 0);
      if (m.baseSN) {
        map.set(m.baseSN, m.power || 0);
      }
    });
    return map;
  }, [zoneEnergyMeterInfo]);

  useEffect(() => {
    if (!zones.length) return;
    if (!selectedZoneForEnergy || !zones.some(z => z.id === selectedZoneForEnergy)) {
      setSelectedZoneForEnergy(zones[0].id);
    }
  }, [zones, selectedZoneForEnergy]);

  const openZoneEnergyMeterModal = () => {
    if (!zones.length) return;
    const zoneId = selectedZoneForEnergy || zones[0].id;
    setSelectedZoneForEnergy(zoneId);
    setTempSelectedZoneEnergyMeters(zoneEnergyMeterMap[zoneId] || []);
    setShowZoneEnergyMeterModal(true);
  };

  // Generate zone energy data using selected meters
  const zoneEnergyData = useMemo(() => {
    if (zones.length === 0) {
      // Fallback to demo zones
      return initialDemoZones.map((z, i) => ({
        zone: z.name.split(' - ')[1] || z.name, // Extract short name
        consumption: DEMO_CONSUMPTION_VALUES[i % DEMO_CONSUMPTION_VALUES.length],
      }));
    }
    
    return zones.map((zone) => {
      const meterIds = zoneEnergyMeterMap[zone.id] || [];
      const totalPower = meterIds.reduce((sum, id) => sum + (powerById.get(id) || 0), 0);
      return {
        zone: zone.name.length > 5 ? zone.name.substring(0, 5) + '..' : zone.name,
        fullName: zone.name,
        consumption: totalPower,
      };
    });
  }, [zones, zoneEnergyMeterMap, powerById]);
  
  // Filter HVAC devices (AC units that can be controlled)
  const hvacDevices = useMemo(() => 
    devices.filter(d => 
      d.deviceType === 'hvac' || 
      d.deviceType === 'ac' ||
      d.deviceType === 'air_conditioner' ||
      d.deviceType === 'AC_UNITS' ||
      d.traits?.some(t => ['TemperatureSetting', 'FanSpeed', 'ThermostatTemperatureSetpoint'].includes(t))
    ), [devices]);
  
  // Calculate master states from actual HVAC devices
  const { masterPower, masterSetpoint, currentMode: derivedMode } = useMemo(() => {
    if (hvacDevices.length === 0) {
      return { masterPower: false, masterSetpoint: 23, currentMode: 'cooling' as const };
    }
    
    // Check if ANY device is ON
    const anyOn = hvacDevices.some(d => d.isOn === true);
    
    // Calculate average setpoint from devices that have it
    const setpoints = hvacDevices
      .filter(d => d.setpoint !== undefined && d.setpoint !== null)
      .map(d => d.setpoint as number);
    const avgSetpoint = setpoints.length > 0 
      ? Math.round(setpoints.reduce((a, b) => a + b, 0) / setpoints.length)
      : 23;
    
    // Get most common mode
    const modes = hvacDevices
      .filter(d => d.mode)
      .map(d => d.mode as string);
    const modeMap: Record<string, number> = {};
    modes.forEach(m => { modeMap[m] = (modeMap[m] || 0) + 1; });
    const topMode = Object.entries(modeMap).sort((a, b) => b[1] - a[1])[0]?.[0];
    
    let normalizedMode: 'cooling' | 'heating' | 'auto' = 'cooling';
    if (topMode === 'heat' || topMode === 'heating') normalizedMode = 'heating';
    else if (topMode === 'auto') normalizedMode = 'auto';
    
    return { 
      masterPower: anyOn, 
      masterSetpoint: avgSetpoint, 
      currentMode: normalizedMode 
    };
  }, [hvacDevices]);
  
  // Local UI states for optimistic updates
  const [localPower, setLocalPower] = useState<boolean | null>(null);
  const [localSetpoint, setLocalSetpoint] = useState<number | null>(null);
  const [localMode, setLocalMode] = useState<'cooling' | 'heating' | 'auto' | null>(null);
  
  // Effective states (local overrides actual during command)
  const effectivePower = localPower !== null ? localPower : masterPower;
  const effectiveSetpoint = localSetpoint !== null ? localSetpoint : masterSetpoint;
  const effectiveMode = localMode !== null ? localMode : derivedMode;
  
  // Reset local states when actual state changes (command completed)
  useEffect(() => {
    if (!isCommandLoading) {
      setLocalPower(null);
      setLocalSetpoint(null);
      setLocalMode(null);
    }
  }, [masterPower, masterSetpoint, derivedMode, isCommandLoading]);
  
  // Get all HVAC device IDs for commands
  const hvacDeviceIds = useMemo(() => hvacDevices.map(d => d.deviceId), [hvacDevices]);
  
  // Handle Master ON/OFF Toggle
  const handleMasterPowerToggle = useCallback(async () => {
    if (hvacDeviceIds.length === 0) return;
    
    const newPower = !effectivePower;
    setLocalPower(newPower);
    setIsCommandLoading(true);
    
    try {
      const command = newPower ? 'on' : 'off';
      await ultronTuringApi.executeCommand(command, hvacDeviceIds);
    } catch (error) {
      console.error('Failed to toggle HVAC power:', error);
      setLocalPower(null); // Rollback on error
    } finally {
      setIsCommandLoading(false);
    }
  }, [hvacDeviceIds, effectivePower]);
  
  // Handle Mode Change
  const handleModeChange = useCallback(async (mode: 'cooling' | 'heating' | 'auto') => {
    if (hvacDeviceIds.length === 0 || !effectivePower) return;
    
    setLocalMode(mode);
    setIsCommandLoading(true);
    
    try {
      // Map to API mode format
      const apiMode = mode === 'cooling' ? 'cool' : mode === 'heating' ? 'heat' : 'auto';
      await ultronTuringApi.executeCommand('setMode', hvacDeviceIds, { mode: apiMode });
    } catch (error) {
      console.error('Failed to set HVAC mode:', error);
      setLocalMode(null); // Rollback on error
    } finally {
      setIsCommandLoading(false);
    }
  }, [hvacDeviceIds, effectivePower]);
  
  // Handle Temperature Change (with debounce for slider)
  const handleTemperatureChange = useCallback(async (newTemp: number, immediate = false) => {
    if (hvacDeviceIds.length === 0 || !effectivePower) return;
    
    const clampedTemp = Math.min(30, Math.max(16, newTemp));
    setLocalSetpoint(clampedTemp);
    
    // Clear existing debounce
    if (commandDebounce.current) {
      clearTimeout(commandDebounce.current);
    }
    
    const sendCommand = async () => {
      setIsCommandLoading(true);
      try {
        await ultronTuringApi.executeCommand('setTemperature', hvacDeviceIds, { temperature: clampedTemp });
      } catch (error) {
        console.error('Failed to set HVAC temperature:', error);
        setLocalSetpoint(null); // Rollback on error
      } finally {
        setIsCommandLoading(false);
      }
    };
    
    if (immediate) {
      await sendCommand();
    } else {
      // Debounce slider changes
      commandDebounce.current = setTimeout(sendCommand, 500);
    }
  }, [hvacDeviceIds, effectivePower]);
  
  // Helper function to calculate zone data from real devices
  const calculateZoneData = useCallback((zoneDeviceSNs: string[]) => {
    const zoneDevices = devices.filter(d => 
      zoneDeviceSNs.includes(d.baseSN) || zoneDeviceSNs.includes(d.deviceId)
    );
    
    // Filter HVAC devices in this zone
    const zoneHvacDevices = zoneDevices.filter(d =>
      d.deviceType === 'hvac' ||
      d.deviceType === 'ac' ||
      d.deviceType === 'air_conditioner' ||
      d.deviceType === 'AC_UNITS' ||
      d.traits?.some(t => ['TemperatureSetting', 'FanSpeed', 'ThermostatTemperatureSetpoint'].includes(t))
    );
    
    const temps: number[] = [];
    const humidities: number[] = [];
    const setpoints: number[] = [];
    const modes: string[] = [];
    let anyHvacOn = false;
    let hasHvacDevices = zoneHvacDevices.length > 0;
    
    zoneDevices.forEach(device => {
      // Check direct properties for temperature/humidity
      if (device.temperature !== undefined && device.temperature !== null && !isNaN(device.temperature)) {
        temps.push(device.temperature);
      }
      if (device.humidity !== undefined && device.humidity !== null && !isNaN(device.humidity)) {
        humidities.push(device.humidity);
      }
      
      // Check attributes for sensor readings
      if (device.attributes) {
        const attrs = device.attributes;
        const tempValue = attrs.temperature ?? attrs.currentTemperature ?? attrs.temperatureAmbientCelsius;
        if (tempValue !== undefined && tempValue !== null && !isNaN(tempValue)) {
          temps.push(Number(tempValue));
        }
        
        const humidityValue = attrs.humidity ?? attrs.humidityAmbientPercent ?? attrs.currentHumidity;
        if (humidityValue !== undefined && humidityValue !== null && !isNaN(humidityValue)) {
          humidities.push(Number(humidityValue));
        }
      }
    });
    
    // Extract HVAC-specific data
    zoneHvacDevices.forEach(device => {
      // Check ON/OFF state
      if (device.isOn === true) {
        anyHvacOn = true;
      }
      
      // Check setpoint (target temperature)
      if (device.setpoint !== undefined && device.setpoint !== null && !isNaN(device.setpoint)) {
        setpoints.push(device.setpoint);
      }
      
      // Check mode
      if (device.mode) {
        modes.push(device.mode);
      }
      
      // Also check attributes for HVAC data
      if (device.attributes) {
        const attrs = device.attributes;
        
        // ON/OFF from attributes
        if (attrs.on === true || attrs.isOn === true) {
          anyHvacOn = true;
        }
        
        // Setpoint from attributes
        const setpointValue = attrs.thermostatTemperatureSetpoint ?? attrs.setpoint ?? attrs.targetTemperature;
        if (setpointValue !== undefined && setpointValue !== null && !isNaN(setpointValue)) {
          setpoints.push(Number(setpointValue));
        }
        
        // Mode from attributes
        const modeValue = attrs.thermostatMode ?? attrs.mode;
        if (modeValue) {
          modes.push(modeValue);
        }
      }
    });
    
    // Normalize mode to our format
    const normalizeMode = (mode: string): 'cool' | 'heat' | 'auto' => {
      const m = mode.toLowerCase();
      if (m === 'cool' || m === 'cooling') return 'cool';
      if (m === 'heat' || m === 'heating') return 'heat';
      return 'auto';
    };
    
    // Get most common mode
    const modeMap: Record<string, number> = {};
    modes.forEach(m => { 
      const normalized = normalizeMode(m);
      modeMap[normalized] = (modeMap[normalized] || 0) + 1; 
    });
    const topMode = Object.entries(modeMap).sort((a, b) => b[1] - a[1])[0]?.[0] as 'cool' | 'heat' | 'auto' | undefined;
    
    return {
      avgTemp: temps.length > 0 ? Math.round(temps.reduce((a, b) => a + b, 0) / temps.length) : null,
      avgHumidity: humidities.length > 0 ? Math.round(humidities.reduce((a, b) => a + b, 0) / humidities.length) : null,
      avgSetpoint: setpoints.length > 0 ? Math.round(setpoints.reduce((a, b) => a + b, 0) / setpoints.length) : null,
      mode: topMode || null,
      isOn: hasHvacDevices ? anyHvacOn : null,
      hasHvacDevices,
      hasSensors: temps.length > 0 || humidities.length > 0,
    };
  }, [devices]);
  
  // Calculate zone stats with actual device data
  const zoneStats = useMemo(() => {
    if (zones.length === 0) {
      return initialDemoZones.map(z => ({
        ...z,
        isOn: zoneStates[z.id] ?? z.isOn,
        targetTemp: zoneTargets[z.id] ?? z.targetTemp,
        mode: zoneModes[z.id] ?? z.mode,
      }));
    }
    
    return zones.map((zone, index) => {
      const demo = initialDemoZones[index % initialDemoZones.length];
      const zoneData = calculateZoneData(zone.devices);
      
      // Priority: local state (for optimistic updates) > real device data > demo
      // isOn: use local state if set, otherwise use real device state, fallback to demo
      const realIsOn = zoneData.isOn;
      const effectiveIsOn = zoneStates[zone.id] !== undefined
        ? zoneStates[zone.id]
        : (realIsOn !== null ? realIsOn : false);
      
      // mode: use local state if set, otherwise use real device mode, fallback to demo
      const realMode = zoneData.mode;
      const effectiveMode = zoneModes[zone.id] ?? realMode ?? demo.mode;
      
      // targetTemp: use local state if set, otherwise use real device setpoint, fallback to demo
      const realSetpoint = zoneData.avgSetpoint;
      const effectiveTargetTemp = zoneTargets[zone.id] ?? realSetpoint ?? demo.targetTemp;
      
      // currentTemp & humidity: use real sensor data, fallback to demo
      const effectiveCurrentTemp = zoneData.avgTemp ?? demo.currentTemp;
      const effectiveHumidity = zoneData.avgHumidity ?? demo.humidity;
      
      return {
        id: zone.id,
        name: zone.name,
        currentTemp: effectiveCurrentTemp,
        targetTemp: effectiveTargetTemp,
        humidity: effectiveHumidity,
        mode: effectiveMode,
        isOn: effectiveIsOn,
        hasHvacDevices: zoneData.hasHvacDevices,
        hasSensors: zoneData.hasSensors,
      };
    });
  }, [zones, zoneStates, zoneTargets, zoneModes, calculateZoneData]);
  
  // Helper to get HVAC device IDs for a specific zone
  const getZoneHvacDeviceIds = useCallback((zoneId: string): string[] => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return [];
    
    return devices
      .filter(d => 
        (zone.devices.includes(d.baseSN) || zone.devices.includes(d.deviceId)) &&
        (d.deviceType === 'hvac' ||
         d.deviceType === 'ac' ||
         d.deviceType === 'air_conditioner' ||
         d.deviceType === 'AC_UNITS' ||
         d.traits?.some(t => ['TemperatureSetting', 'FanSpeed', 'ThermostatTemperatureSetpoint'].includes(t)))
      )
      .map(d => d.deviceId);
  }, [zones, devices]);
  
  // Toggle zone ON/OFF - sends command to zone's HVAC devices
  const handleToggleZone = useCallback(async (zoneId: string) => {
    const currentZone = zoneStats.find(z => z.id === zoneId);
    const newState = !currentZone?.isOn;
    
    // Optimistic update
    setZoneStates(prev => ({
      ...prev,
      [zoneId]: newState,
    }));
    
    // Send command to actual devices
    const zoneDeviceIds = getZoneHvacDeviceIds(zoneId);
    if (zoneDeviceIds.length > 0) {
      try {
        const command = newState ? 'on' : 'off';
        await ultronTuringApi.executeCommand(command, zoneDeviceIds);
      } catch (error) {
        console.error('Failed to toggle zone HVAC:', error);
        // Rollback on error
        setZoneStates(prev => ({
          ...prev,
          [zoneId]: !newState,
        }));
      }
    }
  }, [zoneStats, getZoneHvacDeviceIds]);
  
  // Adjust zone target temperature - sends command to zone's HVAC devices
  const handleAdjustZoneTemp = useCallback(async (zoneId: string, delta: number) => {
    const currentZone = zoneStats.find(z => z.id === zoneId);
    const currentTarget = currentZone?.targetTemp || 22;
    const newTarget = Math.min(32, Math.max(16, currentTarget + delta));
    
    // Optimistic update
    setZoneTargets(prev => ({
      ...prev,
      [zoneId]: newTarget,
    }));
    
    // Send command to actual devices
    const zoneDeviceIds = getZoneHvacDeviceIds(zoneId);
    if (zoneDeviceIds.length > 0) {
      try {
        await ultronTuringApi.executeCommand('setTemperature', zoneDeviceIds, { temperature: newTarget });
      } catch (error) {
        console.error('Failed to set zone temperature:', error);
        // Rollback on error
        setZoneTargets(prev => ({
          ...prev,
          [zoneId]: currentTarget,
        }));
      }
    }
  }, [zoneStats, getZoneHvacDeviceIds]);
  
  // Change zone mode - sends command to zone's HVAC devices
  const handleZoneModeChange = useCallback(async (zoneId: string, mode: 'cool' | 'heat' | 'auto') => {
    const currentZone = zoneStats.find(z => z.id === zoneId);
    const previousMode = currentZone?.mode || 'cool';
    
    // Optimistic update
    setZoneModes(prev => ({
      ...prev,
      [zoneId]: mode,
    }));
    
    // Send command to actual devices
    const zoneDeviceIds = getZoneHvacDeviceIds(zoneId);
    if (zoneDeviceIds.length > 0) {
      try {
        // Map to API mode format
        const apiMode = mode === 'cool' ? 'cool' : mode === 'heat' ? 'heat' : 'auto';
        await ultronTuringApi.executeCommand('setMode', zoneDeviceIds, { mode: apiMode });
      } catch (error) {
        console.error('Failed to set zone mode:', error);
        // Rollback on error
        setZoneModes(prev => ({
          ...prev,
          [zoneId]: previousMode,
        }));
      }
    }
  }, [zoneStats, getZoneHvacDeviceIds]);
  
  // Calculate summary stats
  const activeCount = zoneStats.filter(z => z.isOn).length;
  const offCount = zoneStats.length - activeCount;
  
  // Weather API state
  const [selectedLocation, setSelectedLocation] = useState<string>(() => getSavedLocation() || '');
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  
  // Fetch weather data
  const fetchWeather = useCallback(async (location: string) => {
    setIsWeatherLoading(true);
    try {
      const data = await getWeatherByLocation(location);
      if (data) {
        setWeatherData(data);
      }
    } catch (error) {
      console.error('Failed to fetch weather:', error);
    } finally {
      setIsWeatherLoading(false);
    }
  }, []);
  
  // Resolve location by IP if no saved location
  useEffect(() => {
    const saved = getSavedLocation();
    if (saved) {
      setSelectedLocation(saved);
      return;
    }
    let cancelled = false;
    const resolveIpLocation = async () => {
      const ipLocation = await getLocationByIp();
      const resolved = ipLocation || '臺北市';
      if (!cancelled) {
        setSelectedLocation(resolved);
        saveLocation(resolved);
      }
    };
    resolveIpLocation();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch weather on mount and when location changes
  useEffect(() => {
    if (!selectedLocation) return;
    fetchWeather(selectedLocation);
  }, [selectedLocation, fetchWeather]);
  
  // Handle location selection
  const handleSelectLocation = (location: string) => {
    setSelectedLocation(location);
    saveLocation(location);
    setShowLocationSelector(false);
    setLocationSearch('');
  };
  
  // Filter locations based on search
  const filteredLocations = useMemo(() => {
    if (!locationSearch) return TAIWAN_LOCATIONS;
    return TAIWAN_LOCATIONS.filter(loc => 
      loc.toLowerCase().includes(locationSearch.toLowerCase())
    );
  }, [locationSearch]);
  
  // Power Meter Selector state
  const [selectedPowerMeter, setSelectedPowerMeter] = useState<string>(() => {
    try {
      return localStorage.getItem('hvac-selected-power-meter') || '';
    } catch {
      return '';
    }
  });
  const [showPowerMeterSelector, setShowPowerMeterSelector] = useState(false);
  const [powerMeterSearch, setPowerMeterSearch] = useState('');
  
  // Get all meter devices (power_meter, or any device with 'meter' in type/traits)
  const powerMeters = useMemo(() => {
    return devices.filter(d => 
      d.deviceType === 'power_meter' || 
      d.deviceType?.toLowerCase().includes('meter') ||
      d.traits?.some(t => t.toLowerCase().includes('meter')) ||
      d.watt !== undefined ||
      d.real_power !== undefined
    );
  }, [devices]);
  
  // Filter power meters based on search (include nickname, deviceName in search)
  const filteredPowerMeters = useMemo(() => {
    if (!powerMeterSearch) return powerMeters;
    const search = powerMeterSearch.toLowerCase();
    return powerMeters.filter(pm => {
      const sn = (pm.deviceSN || pm.sn || pm.deviceId || pm.baseSN || '').toLowerCase();
      const nickname = (pm.nickname || '').toLowerCase();
      const deviceName = (pm.deviceName || pm.displayName || pm.name || '').toLowerCase();
      return sn.includes(search) || nickname.includes(search) || deviceName.includes(search);
    });
  }, [powerMeters, powerMeterSearch]);
  
  // Helper to get device SN (unique identifier)
  const getDeviceSN = (d: Device) => d.deviceSN || d.sn || d.deviceId || d.baseSN;
  
  // Handle power meter selection
  const handleSelectPowerMeter = (sn: string) => {
    setSelectedPowerMeter(sn);
    try {
      localStorage.setItem('hvac-selected-power-meter', sn);
    } catch {}
    setShowPowerMeterSelector(false);
    setPowerMeterSearch('');
  };
  
  // Calculate indoor temp & humidity from actual sensor data
  const { avgIndoorTemp, avgHumidity, tempSensorCount, humiditySensorCount } = useMemo(() => {
    // Get devices with temperature readings
    const devicesWithTemp = devices.filter(d => 
      d.temperature !== undefined && d.temperature !== null && !isNaN(d.temperature)
    );
    
    // Get devices with humidity readings
    const devicesWithHumidity = devices.filter(d => 
      d.humidity !== undefined && d.humidity !== null && !isNaN(d.humidity)
    );
    
    // Also check attributes for sensor data
    const allTemps: number[] = [];
    const allHumidities: number[] = [];
    
    devices.forEach(device => {
      // Check direct properties
      if (device.temperature !== undefined && device.temperature !== null && !isNaN(device.temperature)) {
        allTemps.push(device.temperature);
      }
      if (device.humidity !== undefined && device.humidity !== null && !isNaN(device.humidity)) {
        allHumidities.push(device.humidity);
      }
      
      // Check attributes for sensor readings
      if (device.attributes) {
        const attrs = device.attributes;
        // Temperature from various attribute names
        const tempValue = attrs.temperature ?? attrs.currentTemperature ?? attrs.temperatureAmbientCelsius;
        if (tempValue !== undefined && tempValue !== null && !isNaN(tempValue)) {
          if (!allTemps.includes(tempValue)) {
            allTemps.push(Number(tempValue));
          }
        }
        
        // Humidity from various attribute names
        const humidityValue = attrs.humidity ?? attrs.humidityAmbientPercent ?? attrs.currentHumidity;
        if (humidityValue !== undefined && humidityValue !== null && !isNaN(humidityValue)) {
          if (!allHumidities.includes(humidityValue)) {
            allHumidities.push(Number(humidityValue));
          }
        }
      }
    });
    
    // Calculate averages
    const avgTemp = allTemps.length > 0 
      ? Math.round((allTemps.reduce((a, b) => a + b, 0) / allTemps.length) * 10) / 10
      : null;
    
    const avgHum = allHumidities.length > 0
      ? Math.round(allHumidities.reduce((a, b) => a + b, 0) / allHumidities.length)
      : null;
    
    return {
      avgIndoorTemp: avgTemp,
      avgHumidity: avgHum,
      tempSensorCount: allTemps.length,
      humiditySensorCount: allHumidities.length,
    };
  }, [devices]);
  
  // Use calculated values or fallback to demo values
  const indoorTemp = avgIndoorTemp ?? 22.5;
  const outdoorTemp = weatherData?.temperature ?? 28;
  const humidity = avgHumidity ?? 45;
  const efficiency = 89;
  
  // Generate outdoor temperature curve (use forecast when available; no fake history)
  const outdoorCurve = useMemo(() => {
    const curve: (number | null)[] = new Array(24).fill(null);
    const now = new Date();
    const currentHour = now.getHours();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    
    const hasHourlyForecastToday = !!weatherData?.hourlyForecast?.some(point =>
      point.time >= todayStart && point.time < todayEnd
    );
    
    if (hasHourlyForecastToday) {
      weatherData?.hourlyForecast?.forEach(point => {
        const pointTime = point.time;
        if (pointTime >= todayStart && pointTime < todayEnd) {
          const hour = pointTime.getHours();
          curve[hour] = point.temperature;
        }
      });
      return curve;
    }
    
    // No hourly forecast for today: fall back to observation-derived curve (best effort)
    if (weatherData?.observation) {
      const obs = weatherData.observation;
      const currentTemp = obs.currentTemp;
      const lowHour = obs.dailyLow?.time?.getHours() ?? 6;
      const lowTemp = obs.dailyLow?.temp ?? currentTemp - 3;
      const highHour = obs.dailyHigh?.time?.getHours() ?? 12;
      const highTemp = obs.dailyHigh?.temp ?? currentTemp + 2;
      
      for (let h = 0; h <= currentHour; h++) {
        if (h <= lowHour) {
          const midnightEstimate = lowTemp - 1;
          const progress = lowHour > 0 ? h / lowHour : 0;
          curve[h] = Math.round((midnightEstimate + (lowTemp - midnightEstimate) * progress) * 10) / 10;
        } else if (h <= highHour) {
          const denom = Math.max(1, highHour - lowHour);
          const progress = (h - lowHour) / denom;
          curve[h] = Math.round((lowTemp + (highTemp - lowTemp) * progress) * 10) / 10;
        } else {
          const denom = Math.max(1, currentHour - highHour);
          const progress = (h - highHour) / denom;
          curve[h] = Math.round((highTemp + (currentTemp - highTemp) * progress) * 10) / 10;
        }
      }
    }
    
    return curve;
  }, [weatherData?.observation, weatherData?.hourlyForecast]);
  
  // Generate temperature history data - stable reference
  const tempHistoryData = useMemo(() => {
    return Array.from({ length: 24 }, (_, hourIndex) => {
      const hour = hourIndex.toString().padStart(2, '0') + ':00';
      const climatePoint = climateChartData?.[hourIndex];
      const actualTemp = climatePoint?.temp ?? null;
      
      return {
        time: hour,
        actual: actualTemp,
        target: effectiveSetpoint,
        outdoor: outdoorCurve[hourIndex],
      };
    });
  }, [climateChartData, effectiveSetpoint, outdoorCurve]);
  
  // Memoize chart components to prevent re-renders from unrelated state changes
  const temperatureHistoryChart = useMemo(() => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={tempHistoryData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
        <XAxis 
          dataKey="time" 
          tick={{ fontSize: 10, fill: '#64748b' }}
          axisLine={{ stroke: '#334155' }}
          tickLine={false}
          interval={3}
        />
        <YAxis 
          domain={[10, 35]}
          ticks={[10, 15, 20, 25, 30, 35]}
          tick={{ fontSize: 10, fill: '#64748b' }}
          axisLine={false}
          tickLine={false}
          width={30}
          allowDataOverflow={false}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#0d1424', 
            border: '1px solid #1e293b', 
            borderRadius: '8px',
            fontSize: '14px'
          }}
          labelStyle={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}
          formatter={(value, name) => {
            const labels: Record<string, string> = {
              actual: 'Indoor',
              target: 'Target',
              outdoor: 'Outdoor'
            };
            const label = typeof name === 'string' ? name : '';
            const displayValue = typeof value === 'number' ? `${value}°C` : '--';
            return [displayValue, labels[label] || label];
          }}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="actual"
          stroke="#06b6d4"
          strokeWidth={2}
          dot={false}
          name="Actual"
          connectNulls
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="target"
          stroke="#22c55e"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          name="Target"
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="outdoor"
          stroke="#f59e0b"
          strokeWidth={2}
          dot={false}
          name="Outdoor"
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  ), [tempHistoryData]);
  
  const zoneEnergyChart = useMemo(() => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={zoneEnergyData}>
        <XAxis 
          dataKey="zone" 
          tick={{ fontSize: 10, fill: '#64748b' }}
          axisLine={{ stroke: '#334155' }}
          tickLine={false}
        />
        <YAxis 
          tick={{ fontSize: 10, fill: '#64748b' }}
          axisLine={false}
          tickLine={false}
          allowDataOverflow={false}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#0d1424', 
            border: '1px solid #1e293b', 
            borderRadius: '8px',
            fontSize: '14px'
          }}
          labelStyle={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}
          labelFormatter={(label, payload) => {
            const fullName = payload?.[0]?.payload?.fullName;
            return fullName || label;
          }}
          formatter={(value) => {
            const displayValue = typeof value === 'number' ? `${value} kWh` : '--';
            return [displayValue, '用電量'];
          }}
          isAnimationActive={false}
        />
        <Bar 
          dataKey="consumption" 
          fill="#3b82f6" 
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  ), [zoneEnergyData]);

  const handleSelectZoneForEnergy = (zoneId: string) => {
    setSelectedZoneForEnergy(zoneId);
    setTempSelectedZoneEnergyMeters(zoneEnergyMeterMap[zoneId] || []);
  };

  const toggleZoneEnergyMeter = (deviceId: string) => {
    setTempSelectedZoneEnergyMeters(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    );
  };

  const renderZoneEnergyMeterModal = () => {
    if (!showZoneEnergyMeterModal) return null;

    const filteredMeters = zoneEnergyMeterInfo.filter(m => {
      if (!zoneEnergyMeterSearch) return true;
      const search = zoneEnergyMeterSearch.toLowerCase();
      return m.name.toLowerCase().includes(search) || m.id.toLowerCase().includes(search);
    });

    const totalPower = tempSelectedZoneEnergyMeters.reduce((sum, id) => sum + (powerById.get(id) || 0), 0);
    const formatPower = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(2)} kW` : `${Math.round(v)} W`;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowZoneEnergyMeterModal(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-500" />
              Zone Energy Meters
            </h3>
            <button onClick={() => setShowZoneEnergyMeterModal(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex h-[70vh]">
            {/* Left: Zones */}
            <div className="w-1/3 border-r border-slate-700/30 p-4 overflow-y-auto">
              <h4 className="text-sm text-slate-400 mb-3">選擇區域</h4>
              <div className="space-y-2">
                {zones.map(zone => (
                  <button
                    key={zone.id}
                    onClick={() => handleSelectZoneForEnergy(zone.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      selectedZoneForEnergy === zone.id
                        ? 'bg-cyan-900/20 border-cyan-700/50 text-white'
                        : 'bg-slate-800/50 border-slate-700/40 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-sm font-medium truncate">{zone.name}</div>
                    <div className="text-xs text-slate-500">{zone.devices?.length || 0} devices</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Meters */}
            <div className="flex-1 p-4 flex flex-col overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <input
                  value={zoneEnergyMeterSearch}
                  onChange={e => setZoneEnergyMeterSearch(e.target.value)}
                  placeholder="搜尋電錶"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {filteredMeters.length === 0 ? (
                  <p className="text-center text-slate-400 py-6">沒有可用的電錶</p>
                ) : (
                  filteredMeters.map(meter => (
                    <div
                      key={meter.id}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        tempSelectedZoneEnergyMeters.includes(meter.id)
                          ? 'bg-cyan-900/20 border border-cyan-700/50'
                          : 'bg-slate-800/50 hover:bg-slate-700/50'
                      }`}
                      onClick={() => toggleZoneEnergyMeter(meter.id)}
                    >
                      <div className="flex items-center gap-3">
                        {tempSelectedZoneEnergyMeters.includes(meter.id) ? (
                          <CheckSquare className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm text-white">{meter.name}</div>
                          <div className="text-xs text-slate-500">{meter.id}</div>
                          {meter.locationName && (
                            <div className="text-xs text-slate-500">{meter.locationName}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">{formatPower(meter.power)}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="pt-3 mt-3 border-t border-slate-700/30 flex items-center justify-between">
                <div className="text-xs text-slate-400">
                  {tempSelectedZoneEnergyMeters.length} 已選擇 · 總功率 {formatPower(totalPower)}
                </div>
                <button
                  onClick={() => {
                    if (!selectedZoneForEnergy) return;
                    setZoneEnergyMeterMap(prev => {
                      const next = { ...prev, [selectedZoneForEnergy]: tempSelectedZoneEnergyMeters };
                      try {
                        localStorage.setItem(ZONE_ENERGY_METER_MAP_KEY, JSON.stringify(next));
                      } catch {}
                      return next;
                    });
                    setShowZoneEnergyMeterModal(false);
                  }}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
                >
                  儲存
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Control Cards - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Master Temperature - Horizontal Layout */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-cyan-400" />
              <span className="text-base font-semibold text-white">Master Temperature</span>
            </div>
            {/* ON/OFF Toggle */}
            <button
              onClick={handleMasterPowerToggle}
              disabled={isCommandLoading || hvacDevices.length === 0}
              className={`p-2 rounded-lg transition-colors ${
                effectivePower 
                  ? 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30' 
                  : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              } ${isCommandLoading ? 'opacity-50' : ''} ${hvacDevices.length === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
              title={hvacDevices.length === 0 ? 'No HVAC devices' : effectivePower ? 'Turn ALL OFF' : 'Turn ALL ON'}
            >
              {isCommandLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            </button>
          </div>
          
          <div className={`flex items-center gap-6 ${!effectivePower ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Circular Temperature Dial - Left Side */}
            <div className="relative w-32 h-32 flex-shrink-0">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#1e293b"
                  strokeWidth="8"
                />
                {/* Progress arc */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={effectivePower ? "url(#tempGradient)" : "#475569"}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${((effectiveSetpoint - 16) / 14) * 264} 264`}
                />
                <defs>
                  <linearGradient id="tempGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={effectiveMode === 'heating' ? '#f97316' : '#06b6d4'} />
                    <stop offset="100%" stopColor={effectiveMode === 'heating' ? '#ef4444' : '#3b82f6'} />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-white">{effectiveSetpoint}°</span>
                <span className="text-base text-slate-400">{effectivePower ? 'Target' : 'OFF'}</span>
              </div>
            </div>
            
            {/* Controls - Right Side */}
            <div className="flex-1 flex flex-col justify-center">
              {/* Slider */}
              <div className="w-full mb-4">
                <input
                  type="range"
                  min="16"
                  max="30"
                  value={effectiveSetpoint}
                  onChange={(e) => handleTemperatureChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, ${effectiveMode === 'heating' ? '#f97316' : '#06b6d4'} 0%, ${effectiveMode === 'heating' ? '#f97316' : '#06b6d4'} ${((effectiveSetpoint - 16) / 14) * 100}%, #334155 ${((effectiveSetpoint - 16) / 14) * 100}%, #334155 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>16°C</span>
                  <span>{effectiveSetpoint}°C</span>
                  <span>30°C</span>
                </div>
              </div>
              
              {/* Up/Down Buttons + Mode Selector */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTemperatureChange(effectiveSetpoint - 1, true)}
                    className="w-10 h-10 rounded-lg bg-slate-700 text-slate-300 flex items-center justify-center hover:bg-slate-600 transition-colors"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleTemperatureChange(effectiveSetpoint + 1, true)}
                    className="w-10 h-10 rounded-lg bg-slate-700 text-slate-300 flex items-center justify-center hover:bg-slate-600 transition-colors"
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Mode Buttons - Aligned right with ON/OFF */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleModeChange('cooling')}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      effectiveMode === 'cooling' 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="冷氣"
                  >
                    <Snowflake className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleModeChange('heating')}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      effectiveMode === 'heating' 
                        ? 'bg-orange-500/20 text-orange-400' 
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="暖氣"
                  >
                    <Sun className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleModeChange('auto')}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      effectiveMode === 'auto' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="自動"
                  >
                    <Leaf className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Current Conditions */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Thermometer className="w-4 h-4 text-orange-400" />
            <span className="text-base font-semibold text-white">Current Conditions</span>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base text-slate-400">Indoor Temp</span>
                {avgIndoorTemp !== null && (
                  <span className="text-sm text-slate-500">({avgIndoorTemp !== null ? `${tempSensorCount} 感測器` : 'Demo'})</span>
                )}
              </div>
              <span className="text-2xl font-bold text-white">{indoorTemp}°C</span>
            </div>
            
            <div 
              className="flex items-center justify-between cursor-pointer hover:bg-slate-700/30 -mx-2 px-2 py-1 rounded-lg transition-colors group"
              onClick={() => setShowLocationSelector(true)}
            >
              <div className="flex items-center gap-2">
                <span className="text-base text-slate-400">Outdoor Temp</span>
                <div className="flex items-center gap-1 text-sm text-slate-500 group-hover:text-cyan-400 transition-colors">
                  <MapPin className="w-3 h-3" />
                  <span>{selectedLocation}</span>
                </div>
                {weatherData?.observation?.stationName ? (
                  <div className="text-xs text-slate-500">
                    {weatherData.observation.townName ? `${weatherData.observation.townName} · ` : ''}
                    {weatherData.observation.stationName}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {isWeatherLoading ? (
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                ) : (
                  <span className="text-2xl font-bold text-white">{outdoorTemp}°C</span>
                )}
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-cyan-400" />
                  <span className="text-base text-slate-400">Humidity</span>
                  {avgHumidity !== null && (
                    <span className="text-sm text-slate-500">({humiditySensorCount} 感測器)</span>
                  )}
                </div>
                <span className="text-2xl font-bold text-white">{humidity}%</span>
              </div>
              <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                  style={{ width: `${humidity}%` }}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* System Efficiency */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-base font-semibold text-white">System Efficiency</span>
          </div>
          
          <div className="flex items-center justify-between mb-6">
            <span className="text-base text-slate-400">Efficiency</span>
            <span className="text-3xl font-bold text-emerald-400">{efficiency}%</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Leaf className="w-4 h-4 text-emerald-400" />
              <span className="text-base text-slate-400">Eco Mode</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={ecoMode}
                onChange={() => setEcoMode(!ecoMode)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-800/50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
            </label>
          </div>
        </div>
      </div>
      
      {/* Charts Section - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Temperature History */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <h3 className="text-base font-semibold text-white">Temperature History</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">24-hour temperature tracking (°C)</p>
          
          <div className="h-56">
            {temperatureHistoryChart}
          </div>
        </div>
        
        {/* Zone Energy Consumption */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-cyan-400" />
              <h3 className="text-base font-semibold text-white">Zone Energy Consumption</h3>
            </div>
            <button
              onClick={openZoneEnergyMeterModal}
              className="text-slate-400 hover:text-white transition-colors"
              title="設定區域電錶"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-slate-500 mb-4">kWh usage by zone</p>
          
          <div className="h-56">
            {zoneEnergyChart}
          </div>
        </div>
      </div>
      
      {/* Zone Controls Section */}
      <div className="bg-[#0C2340] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Settings className="w-4 h-4 text-slate-400" />
            <div>
              <h3 className="text-base font-semibold text-white">Zone Controls</h3>
              <p className="text-sm text-slate-500">Individual HVAC zone management</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5">
              <Snowflake className="w-3 h-3 text-cyan-400" />
              <span className="text-slate-400">Cooling</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Sun className="w-3 h-3 text-orange-400" />
              <span className="text-slate-400">Heating</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Leaf className="w-3 h-3 text-slate-400" />
              <span className="text-slate-400">Auto</span>
            </span>
            {onOpenZoneSelector && (
              <button
                onClick={onOpenZoneSelector}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
                title="選擇區域"
              >
                <Settings className="w-3 h-3" />
                <span>區域</span>
                {typeof selectedZoneCount === 'number' && (
                  <span className="text-slate-500">({selectedZoneCount})</span>
                )}
              </button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zoneStats.map((zone) => (
            <div
              key={zone.id}
              className={`rounded-lg p-4 border transition-all ${
                zone.isOn 
                  ? 'bg-[#050d18] border-slate-700/50' 
                  : 'bg-[#050d18] border-slate-700/30'
              }`}
            >
              {/* Header: Zone name (with mode icon) + ON/OFF */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {/* Icon reflects current zone mode from zoneModes state */}
                  {zone.mode === 'cool' ? (
                    <Snowflake className={`w-4 h-4 ${zone.isOn ? 'text-cyan-400' : 'text-slate-500'}`} />
                  ) : zone.mode === 'heat' ? (
                    <Sun className={`w-4 h-4 ${zone.isOn ? 'text-orange-400' : 'text-slate-500'}`} />
                  ) : (
                    <Leaf className={`w-4 h-4 ${zone.isOn ? 'text-emerald-400' : 'text-slate-500'}`} />
                  )}
                  <span className={`text-base font-medium ${zone.isOn ? 'text-white' : 'text-slate-500'}`}>
                    {zone.name}
                  </span>
                </div>
                <button 
                  onClick={() => handleToggleZone(zone.id)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    zone.isOn 
                      ? 'text-cyan-400 hover:bg-cyan-400/10' 
                      : 'text-slate-500 hover:bg-slate-700'
                  }`}
                  title={zone.isOn ? 'Turn Off' : 'Turn On'}
                >
                  <Power className="w-4 h-4" />
                </button>
              </div>
              
              {/* Content: Current temp + diff on left, Target controls in center, Mode buttons on right */}
              <div className="flex items-center">
                {/* Current Temperature & Humidity + Temperature Difference - Fixed width */}
                <div className={`w-24 flex-shrink-0 ${zone.isOn ? '' : 'opacity-50'}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-bold text-white">{zone.currentTemp}°</p>
                    {/* Temperature difference indicator - right of current temp */}
                    {zone.isOn && zone.currentTemp !== zone.targetTemp && (
                      <div className={`px-1.5 py-0.5 rounded-full text-sm font-medium ${
                        zone.currentTemp > zone.targetTemp 
                          ? 'bg-orange-500/20 text-orange-400' 
                          : 'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {zone.currentTemp > zone.targetTemp ? '+' : ''}{zone.currentTemp - zone.targetTemp}°
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">Current</p>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <Droplets className="w-3 h-3" />
                    {zone.humidity}%
                  </p>
                </div>
                
                {/* Target Temperature Controls - Centered, flex-1 to take remaining space */}
                <div className={`flex-1 flex items-center justify-center gap-2 ${!zone.isOn ? 'opacity-50 pointer-events-none' : ''}`}>
                  <button
                    onClick={() => handleAdjustZoneTemp(zone.id, -1)}
                    className="w-8 h-8 rounded-lg bg-slate-700 text-slate-300 flex items-center justify-center hover:bg-slate-600 transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  <div className="text-center min-w-[48px]">
                    <p className={`text-2xl font-bold ${zone.isOn ? 'text-cyan-400' : 'text-slate-500'}`}>{zone.targetTemp}°</p>
                    <p className="text-sm text-slate-500">Target</p>
                  </div>
                  
                  <button
                    onClick={() => handleAdjustZoneTemp(zone.id, 1)}
                    className="w-8 h-8 rounded-lg bg-slate-700 text-slate-300 flex items-center justify-center hover:bg-slate-600 transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Mode Buttons - Fixed width */}
                <div className={`flex-shrink-0 flex items-center gap-1 ${!zone.isOn ? 'opacity-50 pointer-events-none' : ''}`}>
                  <button
                    onClick={() => handleZoneModeChange(zone.id, 'cool')}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      zone.mode === 'cool'
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="冷氣"
                  >
                    <Snowflake className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleZoneModeChange(zone.id, 'heat')}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      zone.mode === 'heat'
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="暖氣"
                  >
                    <Sun className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleZoneModeChange(zone.id, 'auto')}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      zone.mode === 'auto'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title="自動"
                  >
                    <Leaf className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Location Selector Modal */}
      {showLocationSelector && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowLocationSelector(false)}
        >
          <div 
            className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-semibold text-white">選擇所在地</h3>
              </div>
              <button 
                onClick={() => setShowLocationSelector(false)}
                className="p-1 hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4 border-b border-slate-700/30">
              <input
                type="text"
                placeholder="搜尋縣市..."
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                autoFocus
              />
            </div>
            
            {/* Location List */}
            <div className="overflow-y-auto max-h-[50vh]">
              {filteredLocations.map((location) => (
                <button
                  key={location}
                  onClick={() => handleSelectLocation(location)}
                  className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors ${
                    selectedLocation === location ? 'bg-cyan-900/20' : ''
                  }`}
                >
                  <span className="text-white">{location}</span>
                  {selectedLocation === location && (
                    <Check className="w-5 h-5 text-cyan-400" />
                  )}
                </button>
              ))}
              
              {filteredLocations.length === 0 && (
                <div className="p-4 text-center text-slate-400">
                  找不到符合的縣市
                </div>
              )}
            </div>
            
            {/* Weather Info Footer */}
            {weatherData && (
              <div className="p-4 border-t border-slate-700/30 bg-slate-800/20">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">目前天氣</span>
                  <span className="text-white">{weatherData.description}</span>
                </div>
                {weatherData.rainProbability && (
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-400">降雨機率</span>
                    <span className="text-cyan-400">{weatherData.rainProbability}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Power Meter Selector Modal */}
      {showPowerMeterSelector && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowPowerMeterSelector(false)}
        >
          <div 
            className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-semibold text-white">選擇電錶</h3>
              </div>
              <button 
                onClick={() => setShowPowerMeterSelector(false)}
                className="p-1 hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4 border-b border-slate-700/30">
              <input
                type="text"
                placeholder="搜尋電錶..."
                value={powerMeterSearch}
                onChange={(e) => setPowerMeterSearch(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                autoFocus
              />
            </div>
            
            {/* Option: Show All (Total) */}
            <div className="border-b border-slate-700/30">
              <button
                onClick={() => handleSelectPowerMeter('')}
                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors ${
                  selectedPowerMeter === '' ? 'bg-cyan-900/20' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <span className="text-white block">全部電錶</span>
                    <span className="text-sm text-slate-500">{powerMeters.length} 個電錶的總和</span>
                  </div>
                </div>
                {selectedPowerMeter === '' && (
                  <Check className="w-5 h-5 text-cyan-400" />
                )}
              </button>
            </div>
            
            {/* Power Meter List */}
            <div className="overflow-y-auto max-h-[40vh]">
              {filteredPowerMeters.map((pm) => {
                const pmId = getDeviceSN(pm);
                const pmNickname = pm.nickname;
                const pmDeviceName = pm.deviceName || pm.displayName || pm.name;
                const pmDisplayName = pmNickname || pmDeviceName || pmId;
                const watt = pm.watt ?? pm.real_power ?? 0;
                const kw = (watt / 1000).toFixed(1);
                
                return (
                  <button
                    key={pmId}
                    onClick={() => handleSelectPowerMeter(pmId)}
                    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors ${
                      selectedPowerMeter === pmId ? 'bg-cyan-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="text-left">
                        <span className="text-white block">{pmDisplayName}</span>
                        <span className="text-sm text-slate-500">
                          {pmNickname ? `${pmId} · ` : ''}{kw} kW
                        </span>
                      </div>
                    </div>
                    {selectedPowerMeter === pmId && (
                      <Check className="w-5 h-5 text-cyan-400" />
                    )}
                  </button>
                );
              })}
              
              {filteredPowerMeters.length === 0 && powerMeters.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">沒有找到電錶</p>
                  <p className="text-xs mt-1">請先匯入電錶設備</p>
                </div>
              )}
              
              {filteredPowerMeters.length === 0 && powerMeters.length > 0 && (
                <div className="p-4 text-center text-slate-400">
                  找不到符合的電錶
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {renderZoneEnergyMeterModal()}
    </div>
  );
};

export default HVACControlView;
